import { useCallback, useEffect, useMemo, useRef } from 'react';
import { toast } from 'react-toastify';
import { isSameMonth } from 'date-fns';
import { useShallow } from 'zustand/react/shallow';
import {
  getSResourceEmptyShift,
  transformRegionViolationsByOrgan,
} from '@lp/features/Scheduler/SchedulerSurgeons/utils';
import {
  createSchedulerDataHook,
  createSchedulerMutationActionsHook,
  createUseShiftChangeHook,
  createUseUnblockShiftHook,
  createWorkdaySyncHook,
} from '@lp/features/Scheduler/factories';
import { getFilteredResources } from '@lp/features/Scheduler/utils';
import { transformResourcesViolations } from '@lp/features/Scheduler/validation.utils';
import { RegionsDataDictionary } from '@lp/features/Scheduler/types';
import {
  createSSchedulerServicesHook,
  getUseSSchedulerFiltersStore,
} from '@lp/features/Scheduler/SchedulerSurgeons/factories';
import { scheduleFiltersSelectors, useSSchedulerHighlightStore, useSSchedulerStore } from '@lp/core/store/scheduler';
import { SOrganGroup } from '@lp/features/Scheduler/SchedulerSurgeons/types';
import {
  PlanningProcess,
  PlanningProcessStatus,
  SRegion,
  SResource,
  SResourcesWithShifts,
} from '@/api/apis/resources/schedule';
import { EmployeeRole } from '@/api/apis/resources';
import {
  useSHrSystemConflictsQuery,
  useHtLuSCancelGenerationPlanningMutation,
  useHtLuSCancelPlanningMutation,
  useHtLuSCreatePlanningMutation,
  useHtLuSHrSystemConflictResolveMutation,
  useSHtLuMonthScheduleQuery,
  useHtLuSPublishPlanningMutation,
  useHtLuSRegeneratePlanningMutation,
  useSHtLuScheduleRegionsQuery,
  useHtLuSScheduleResetShiftTypeOptimisticMutation,
  useHtLuSScheduleSetShiftTypeOptimisticMutation,
  useSHtLuScheduleShiftsQuery,
  useHtLuSScheduleSyncWorkdayMutation,
  useLiSCancelGenerationPlanningMutation,
  useLiSCancelPlanningMutation,
  useLiSCreatePlanningMutation,
  useLiSHrSystemConflictResolveMutation,
  useSLiMonthScheduleQuery,
  useLiSPublishPlanningMutation,
  useLiSRegeneratePlanningMutation,
  useSLiScheduleRegionsQuery,
  useLiSScheduleResetShiftTypeOptimisticMutation,
  useLiSScheduleSetShiftTypeOptimisticMutation,
  useSLiScheduleShiftsQuery,
  useLiSScheduleSyncWorkdayMutation,
  useSPlanningProcessQuery,
  useSValidationQuery,
  useLiSSetFixationTypeOptimisticMutation,
  useHtLuSSetFixationTypeOptimisticMutation,
} from '@/queries/resource/schedule';
import { getFullName } from '@/utils/helpers';

export const useLiSSchedulerData = createSchedulerDataHook({
  useMonthScheduleQuery: useSLiMonthScheduleQuery,
  usePlanningProcessQuery: useSPlanningProcessQuery,
  useScheduleShiftsQuery: useSLiScheduleShiftsQuery,
  employeeRole: EmployeeRole.RetrievalSurgeon,
});
export const useHtLuSSchedulerData = createSchedulerDataHook({
  useMonthScheduleQuery: useSHtLuMonthScheduleQuery,
  usePlanningProcessQuery: useSPlanningProcessQuery,
  useScheduleShiftsQuery: useSHtLuScheduleShiftsQuery,
  employeeRole: EmployeeRole.RetrievalSurgeon,
});

export const useLiSSchedulerMutationActions = createSchedulerMutationActionsHook({
  useCreatePlanningMutation: useLiSCreatePlanningMutation,
  useRegeneratePlanningMutation: useLiSRegeneratePlanningMutation,
  useCancelPlanningMutation: useLiSCancelPlanningMutation,
  usePublishPlanningMutation: useLiSPublishPlanningMutation,
  useCancelGenerationPlanningMutation: useLiSCancelGenerationPlanningMutation,
});
export const useHtLuSSchedulerMutationActions = createSchedulerMutationActionsHook({
  useCreatePlanningMutation: useHtLuSCreatePlanningMutation,
  useRegeneratePlanningMutation: useHtLuSRegeneratePlanningMutation,
  useCancelPlanningMutation: useHtLuSCancelPlanningMutation,
  usePublishPlanningMutation: useHtLuSPublishPlanningMutation,
  useCancelGenerationPlanningMutation: useHtLuSCancelGenerationPlanningMutation,
});

export const useLiSWorkdaySync = createWorkdaySyncHook({
  useWorkdaySyncMutation: useLiSScheduleSyncWorkdayMutation,
  useGetWorkdayConflicts: useSHrSystemConflictsQuery,
  useHrSystemConflictResolveMutation: useLiSHrSystemConflictResolveMutation,
});
export const useHtLuSWorkdaySync = createWorkdaySyncHook({
  useWorkdaySyncMutation: useHtLuSScheduleSyncWorkdayMutation,
  useGetWorkdayConflicts: useSHrSystemConflictsQuery,
  useHrSystemConflictResolveMutation: useHtLuSHrSystemConflictResolveMutation,
});

export const useLiSSchedulerServices = createSSchedulerServicesHook(useSLiScheduleRegionsQuery);
export const useHtLuSSchedulerServices = createSSchedulerServicesHook(useSHtLuScheduleRegionsQuery);

export const useLiSShiftChange = createUseShiftChangeHook({
  useScheduleSetShiftTypeOptimisticMutation: useLiSScheduleSetShiftTypeOptimisticMutation,
  useScheduleResetShiftTypeOptimisticMutation: useLiSScheduleResetShiftTypeOptimisticMutation,
});
export const useHtLuSShiftChange = createUseShiftChangeHook({
  useScheduleSetShiftTypeOptimisticMutation: useHtLuSScheduleSetShiftTypeOptimisticMutation,
  useScheduleResetShiftTypeOptimisticMutation: useHtLuSScheduleResetShiftTypeOptimisticMutation,
});

export const useLiSUnblockShift = createUseUnblockShiftHook({
  useSetFixationTypeOptimisticMutation: useLiSSetFixationTypeOptimisticMutation,
});
export const useHtLuSUnblockShift = createUseUnblockShiftHook({
  useSetFixationTypeOptimisticMutation: useHtLuSSetFixationTypeOptimisticMutation,
});

export const useSScheduleValidation = (
  planningData?: PlanningProcess,
  shiftsData?: SResourcesWithShifts,
  regionsDictionary?: RegionsDataDictionary,
  enabled?: boolean,
) => {
  const { scheduleId, status } = planningData ?? {};
  const isEditing = status === PlanningProcessStatus.Editing;
  const canValidate = Boolean(enabled && scheduleId && isEditing);
  const { data, isFetching } = useSValidationQuery(scheduleId, canValidate);
  const fillingState = data?.scheduleSize.fillingState;

  const { byOrganRegionMap, regionsMessagesList } = useMemo(() => {
    return transformRegionViolationsByOrgan(data, regionsDictionary);
  }, [data, regionsDictionary]);

  const { byResourceMap, resourcesMessagesList } = useMemo(() => {
    return transformResourcesViolations(data?.resourceRulesViolations, shiftsData);
  }, [data?.resourceRulesViolations, shiftsData]);

  const count = useMemo(() => {
    const allMessages = [...regionsMessagesList, ...resourcesMessagesList].flatMap((v) => v.messages);
    return allMessages.length;
  }, [regionsMessagesList, resourcesMessagesList]);

  if (!enabled) return undefined;

  return {
    count,
    byOrganRegionMap,
    byResourceMap,
    regionsMessagesList,
    resourcesMessagesList,
    isFetching,
    fillingState,
    publishReadiness: data?.publishReadiness,
  };
};

type UseSSchedulerShiftHighlightProps = {
  organGroup: SOrganGroup;
  selectedDate: Date;
  daysInterval: Date[];
  regionToResourceMap: Record<string, SResource[]> | null;
  shiftsData?: SResourcesWithShifts;
};
export const useSSchedulerShiftHighlight = ({
  organGroup,
  selectedDate,
  daysInterval,
  regionToResourceMap,
  shiftsData,
}: UseSSchedulerShiftHighlightProps) => {
  const setRegionExpand = useSSchedulerStore.use.setRegionExpand();
  const setHighlightValue = useSSchedulerHighlightStore.use.setHighlightValue();

  return useCallback(
    (resourceId: string) => {
      if (!shiftsData || !regionToResourceMap) return;
      const currentMonthDays = daysInterval.filter((d) => isSameMonth(d, selectedDate));
      const obj = getSResourceEmptyShift(resourceId, currentMonthDays, shiftsData, regionToResourceMap);
      if (obj && obj.regionId && obj.resourceId) {
        setRegionExpand(organGroup, obj.regionId, true);
        setHighlightValue({ organGroup, ...obj });
      } else {
        const resource = shiftsData[resourceId]?.resource;
        const fullName = getFullName(resource?.firstName, resource?.lastName);
        toast.warning(`${fullName} cannot be displayed in the schedule`);
      }
    },
    [daysInterval, organGroup, regionToResourceMap, selectedDate, setHighlightValue, setRegionExpand, shiftsData],
  );
};

type RegionVisibility = {
  visibleRegionIds: Set<string>;
  hasResourceFilters: boolean;
  organGroup: SOrganGroup;
};

export const useSFilteredRegionVisibility = (
  organGroup: SOrganGroup,
  regionList: SRegion[] | undefined,
  regionToResourceMap: Record<string, SResource[]> | null,
): RegionVisibility => {
  const useSSchedulerFiltersStore = getUseSSchedulerFiltersStore(organGroup);
  const hasFilters = useSSchedulerFiltersStore(scheduleFiltersSelectors.hasResourceFilters);
  const filters = useSSchedulerFiltersStore(
    useShallow((s) => ({
      resources: s.resources,
      organs: s.organs,
      phoneSupportOrgans: s.phoneSupportOrgans,
      shadowOrgans: [],
      proctorOrgans: [],
    })),
  );

  const visibleRegionIds = useMemo(() => {
    const regionIds = new Set<string>();

    if (!regionList || !regionToResourceMap) return regionIds;

    if (!hasFilters) {
      regionList.forEach((region) => regionIds.add(region.id));
      return regionIds;
    }

    regionList.forEach((region) => {
      const userList = regionToResourceMap[region.id] ?? [];
      const filteredUsers = getFilteredResources(userList, filters);

      if (filteredUsers.length > 0) {
        regionIds.add(region.id);
      }
    });

    return regionIds;
  }, [hasFilters, regionList, regionToResourceMap, filters]);

  return { visibleRegionIds, hasResourceFilters: hasFilters, organGroup };
};

export const useSAutoExpandOnFilterChange = (regionVisibility: RegionVisibility) => {
  const setRegionExpand = useSSchedulerStore.use.setRegionExpand();

  const prevVisibleRef = useRef<Set<string>>(new Set());

  const { hasResourceFilters, visibleRegionIds, organGroup } = regionVisibility;

  useEffect(() => {
    if (!hasResourceFilters) {
      prevVisibleRef.current = new Set();
      return;
    }

    const prev = prevVisibleRef.current;
    visibleRegionIds.forEach((regionId) => {
      if (!prev.has(regionId)) setRegionExpand(organGroup, regionId, true);
    });

    prevVisibleRef.current = visibleRegionIds;
  }, [hasResourceFilters, visibleRegionIds, setRegionExpand, organGroup]);
};
